// Skill packs (04 §Agents: "Admins: `skill-packs` CRUD"; 02 `skill_pack`; M4-02). Behind the
// `agents` add-on. Readers see the packs the company may use — the platform and agency catalog
// copies (`skill_pack.mirror`) and, later, the company's own — with their files and content hash;
// managers assign a pack to roles, which decides the seats whose runs mount it read-only.
// Publishing and deprecating catalog packs is the platform's (seed, `skill_pack.mirror`); company
// uploads are not part of M4-02 (they need the object store and the review path).
import { z } from "zod";
import { SkillAdditionVia, SkillPackStatus } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { SkillPack, SkillPackFile } from "../skill-pack-rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

export const SkillPackListQuery = PageQuery.extend({
  status: SkillPackStatus.optional(),
  role_id: Uuid.optional().describe("Only packs assigned to this role"),
});

/** A list row: the pack without file contents (paths, sizes and hashes only). */
export const SkillPackSummary = SkillPack.omit({ files: true })
  .extend({ files: z.array(SkillPackFile.omit({ text: true })) })
  .meta({ id: "SkillPackSummary" });

/** A digital employee whose seat holds one of the pack's roles: its runs mount the pack. */
export const SkillPackAssignee = z
  .object({
    membership_id: Uuid,
    display_name: z.string(),
    seat_id: Uuid,
    role_id: Uuid,
  })
  .meta({ id: "SkillPackAssignee" });

export const SkillPackDetail = SkillPack.extend({
  assignees: z.array(SkillPackAssignee),
}).meta({ id: "SkillPackDetail" });

export const SkillPackPatch = z
  .strictObject({
    role_ids: z
      .array(Uuid)
      .max(64)
      .refine((xs) => new Set(xs).size === xs.length, "duplicate role id")
      .optional(),
    /**
     * WF-CONTROL: assign the pack to these roles too, or take it from them, as one change under
     * the pack's lock (a concurrent change of other roles is kept). Not with `role_ids`.
     */
    add_role_ids: z.array(Uuid).min(1).max(64).optional(),
    remove_role_ids: z.array(Uuid).min(1).max(64).optional(),
    /**
     * WF-CONTROL: the digital employees the caller confirmed the change for. When sent, the
     * change is refused (409) if anyone else holds a seat of a role that moves.
     */
    confirm_assignees: z.array(Uuid).max(500).optional(),
    /**
     * WF-CONTROL: the digital employees the change is for. When sent, the change is refused
     * (409) unless each of them still holds a seat of a role that moves (checked under the same
     * locks as the change).
     */
    require_assignees: z.array(Uuid).max(50).optional(),
    /**
     * AGENT-SAFETY: `true` archives the pack (kept, no longer mounted on new runs), `false`
     * restores it. Archiving never deletes; the unused-pack job archives the same way.
     */
    archived: z.boolean().optional(),
  })
  .refine(
    (p) =>
      p.role_ids !== undefined ||
      p.add_role_ids !== undefined ||
      p.remove_role_ids !== undefined ||
      p.archived !== undefined,
    "nothing to change",
  )
  .refine(
    (p) => p.role_ids === undefined || (!p.add_role_ids && !p.remove_role_ids),
    "role_ids replaces the set: send it, or add_role_ids / remove_role_ids, not both",
  )
  .meta({ id: "SkillPackPatch" });

// SKILL-OVERRIDES (founder 2026-10-30): a company can open a skill a DE or helper uses, see our
// default, add its own lines on top (versioned), see how it differs and reset it. The default
// stays the base; the additions apply alongside and win only where they directly conflict.
// A skill with no published pack here lives in a self-hosted runtime (a Hermes container): view
// only, with "Request a change" filed to the support log.

/** A skill's key as the library lists it (a pack key, or a runtime skill's name). */
export const SkillKey = z.string().trim().min(1).max(80);
export const SkillParams = z.object({ co: Uuid, key: SkillKey });

/** Where a skill's text lives: ours (a published pack) or a self-hosted runtime's own files. */
export const SkillHosting = z.enum(["companyos", "self_hosted"]).meta({ id: "SkillHosting" });

/** One version of the company's additions, as the history lists it (newest first). */
export const SkillAdditionVersion = z
  .object({
    n: z.int().min(1),
    text: z.string().nullable(),
    via: SkillAdditionVia,
    base_version: z.string(),
    actor_membership_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "SkillAdditionVersion" });

/** A skill's state in the library list (only skills with a published default or additions). */
export const SkillState = z
  .object({
    key: SkillKey,
    hosting: SkillHosting,
    default_version: z.string().nullable(),
    /** The current additions version (0: never edited). */
    n: z.int().min(0),
    has_additions: z.boolean(),
    /** We shipped a newer default since the additions were last written or reviewed. */
    default_updated: z.boolean(),
  })
  .meta({ id: "SkillState" });

/**
 * A list row: the state, and the DEs whose runs mount the skill now (through their seat's packs:
 * a role's skill-pack assignment, the template layers), so the library lists those too.
 */
export const SkillListItem = SkillState.extend({
  mounted_by: z.array(Uuid).max(1000),
}).meta({ id: "SkillListItem" });

export const SkillDetail = SkillState.extend({
  /** Our default: the published pack's SKILL.md (null for a self-hosted skill). */
  default: z
    .object({
      pack_id: Uuid,
      name: z.string(),
      version: z.string(),
      content_hash: z.string(),
      text: z.string(),
    })
    .nullable(),
  /** The current additions (null: none, or reset to the default). */
  additions: z.string().nullable(),
  /** The default the additions were last written or reviewed against (null: never edited). */
  base_version: z.string().nullable(),
  /** That default's text, while it differs from today's (what changed in "Default updated"). */
  previous_default_text: z.string().nullable(),
  /** The newest versions first (at most 20). */
  versions: z.array(SkillAdditionVersion).max(20),
  /** The caller may write additions or request a change (a manager). */
  can_edit: z.boolean(),
}).meta({ id: "SkillDetail" });

export const SkillAdditionsPut = z
  .strictObject({
    /**
     * The additions (null or blank: reset to the default). The same text again while a newer
     * default waits marks that default reviewed; otherwise the same text writes nothing.
     */
    text: z.string().max(20_000).nullable(),
    /** The version the caller edited (`n`); 409 when another edit landed since. */
    expected_n: z.int().min(0),
    /**
     * The default the caller saw (`default.content_hash`); 409 when we shipped a newer one since,
     * so a save or a review never acknowledges a default nobody looked at.
     */
    default_hash: z
      .string()
      .max(64)
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
  })
  .meta({ id: "SkillAdditionsPut" });

export const SkillChangeRequestCreate = z
  .strictObject({ what: z.string().trim().min(1).max(400) })
  .meta({ id: "SkillChangeRequestCreate" });

export const skillRoutes = {
  "skills.list": defineRoute({
    ...agents,
    id: "skills.list",
    method: "get",
    path: `${base}/skills`,
    tag: "skill-packs",
    summary:
      "Each skill with our default or company additions: hosting, versions and whether a new default waits for review",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(SkillListItem).max(1000) }) },
    errors: [],
    emits: [],
  }),
  "skills.get": defineRoute({
    ...agents,
    id: "skills.get",
    method: "get",
    path: `${base}/skills/{key}`,
    tag: "skill-packs",
    summary:
      "One skill: our default text, the company's additions and their versions, and the previous default when a new one waits",
    access: "viewer",
    params: SkillParams,
    response: { status: 200, schema: SkillDetail },
    errors: [],
    emits: [],
  }),
  "skills.additions.put": defineRoute({
    ...agents,
    id: "skills.additions.put",
    method: "put",
    path: `${base}/skills/{key}/additions`,
    tag: "skill-packs",
    summary:
      "Write the company's additions to one of our skills (a new version), reset them to the default, or mark a new default reviewed",
    access: "manager",
    params: SkillParams,
    body: SkillAdditionsPut,
    response: { status: 200, schema: SkillDetail },
    errors: ["conflict"],
    emits: ["skill_pack.additions_changed"],
  }),
  "skills.changeRequest": defineRoute({
    ...agents,
    id: "skills.changeRequest",
    method: "post",
    path: `${base}/skills/{key}/change-request`,
    tag: "skill-packs",
    summary:
      "Ask for a change to a skill that lives in a self-hosted runtime (filed to the support log)",
    access: "manager",
    params: SkillParams,
    body: SkillChangeRequestCreate,
    // No company event: the entry goes to the operator-only support log, as agentIssues.create.
    // A read for the event rules.
    safe: true,
    response: { status: 202, schema: z.object({ accepted: z.literal(true) }) },
    errors: ["conflict"],
    emits: [],
  }),
} as const;

export const skillPackRoutes = {
  "skillPacks.list": defineRoute({
    ...agents,
    id: "skillPacks.list",
    method: "get",
    path: `${base}/skill-packs`,
    tag: "skill-packs",
    summary: "Skill packs the company may use, with version, content hash and assigned roles",
    access: "viewer",
    params: CompanyParams,
    query: SkillPackListQuery,
    response: { status: 200, schema: page(SkillPackSummary) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "skillPacks.get": defineRoute({
    ...agents,
    id: "skillPacks.get",
    method: "get",
    path: `${base}/skill-packs/{id}`,
    tag: "skill-packs",
    summary: "A skill pack with its files, manifest, hash and the DEs whose runs mount it",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: SkillPackDetail },
    errors: [],
    emits: [],
  }),
  "skillPacks.update": defineRoute({
    ...agents,
    id: "skillPacks.update",
    method: "patch",
    path: `${base}/skill-packs/{id}`,
    tag: "skill-packs",
    summary:
      "Assign a skill pack to roles (their seats' runs mount it), or archive or restore it; the content never changes",
    access: "manager",
    params: CompanyItemParams,
    body: SkillPackPatch,
    response: { status: 200, schema: SkillPackDetail },
    errors: ["conflict"],
    emits: ["skill_pack.roles_changed", "skill_pack.archived", "skill_pack.restored"],
  }),
} as const;
